import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field, FieldArray } from "formik";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  Checkbox,
  Chip,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  Paper,
} from "@material-ui/core";
import {
  Visibility,
  VisibilityOff,
  ExpandMore,
  Add,
  Delete,
  Info,
  Settings,
  Timer,
  Chat,
  Functions,
  SwapHoriz,
  PersonOutline,
  VpnKey,
  Close,
  LocalOffer,
  ListAlt,
  Search,
  ConfirmationNumber,
} from "@material-ui/icons";
import { SiOpenai, SiGoogle } from "react-icons/si";
import { TbBrandOpenai } from "react-icons/tb";
import { FaRobot } from "react-icons/fa";

// Configurações de provedores e modelos
const providers = {
  openai: {
    name: "OpenAI",
    icon: SiOpenai,
    color: "#10a37f",
    models: [
      { id: "gpt-4o", name: "GPT-4o" },
      { id: "gpt-4o-mini", name: "GPT-4o Mini" },
      { id: "gpt-4-turbo", name: "GPT-4 Turbo" },
      { id: "gpt-4", name: "GPT-4" },
      { id: "gpt-3.5-turbo", name: "GPT-3.5 Turbo" },
      { id: "gpt-3.5-turbo-16k", name: "GPT-3.5 Turbo 16K" },
    ],
  },
  gemini: {
    name: "Google Gemini",
    icon: SiGoogle,
    color: "#4285f4",
    models: [
      { id: "gemini-2.0-flash", name: "Gemini 2.0 Flash" },
      { id: "gemini-1.5-pro", name: "Gemini 1.5 Pro" },
      { id: "gemini-1.5-flash", name: "Gemini 1.5 Flash" },
      { id: "gemini-pro", name: "Gemini Pro" },
    ],
  },
  anthropic: {
    name: "Anthropic Claude",
    icon: TbBrandOpenai,
    color: "#cc785c",
    models: [
      { id: "claude-opus-4-20250514", name: "Claude Opus 4" },
      { id: "claude-sonnet-4-20250514", name: "Claude Sonnet 4" },
      { id: "claude-3-5-sonnet-20241022", name: "Claude 3.5 Sonnet" },
      { id: "claude-3-opus-20240229", name: "Claude 3 Opus" },
      { id: "claude-3-haiku-20240307", name: "Claude 3 Haiku" },
    ],
  },
  local: {
    name: "LLM Local (Ollama)",
    icon: FaRobot,
    color: "#6b7280",
    models: [
      { id: "llama3.2", name: "Llama 3.2" },
      { id: "llama3.1", name: "Llama 3.1" },
      { id: "mistral", name: "Mistral" },
      { id: "mixtral", name: "Mixtral" },
      { id: "qwen2.5", name: "Qwen 2.5" },
      { id: "custom", name: "Personalizado" },
    ],
  },
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    gap: theme.spacing(1),
    "& > *": {
      flex: 1,
    },
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  sectionTitle: {
    fontWeight: "bold",
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  providerTab: {
    minWidth: 100,
    textTransform: "none",
    fontWeight: "bold",
  },
  providerTabPanel: {
    padding: theme.spacing(2),
  },
  accordion: {
    marginBottom: theme.spacing(1),
    "&:before": {
      display: "none",
    },
  },
  accordionSummary: {
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.05)"
        : "rgba(0, 0, 0, 0.02)",
    borderRadius: theme.spacing(0.5),
    minHeight: 48,
    "&.Mui-expanded": {
      minHeight: 48,
    },
  },
  accordionDetails: {
    padding: theme.spacing(2),
    flexDirection: "column",
  },
  providerCard: {
    border: `2px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.05)"
          : "rgba(0, 0, 0, 0.02)",
    },
  },
  providerCardSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.08)"
        : "rgba(0, 0, 0, 0.04)",
  },
  temporarySettings: {
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.03)"
        : "rgba(0, 0, 0, 0.02)",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  keywordChip: {
    margin: 2,
  },
}));

// Esquema de validação
const IAAgentSchema = Yup.object().shape({
  provider: Yup.string()
    .oneOf(["openai", "gemini", "anthropic", "local"], "Provedor inválido")
    .required("Selecione um provedor"),
  model: Yup.string().required("Selecione um modelo"),
  apiKey: Yup.string().when("provider", {
    is: (provider) => provider !== "local",
    then: Yup.string().required("API Key obrigatória"),
    otherwise: Yup.string(),
  }),
  prompt: Yup.string()
    .min(20, "Muito curto!")
    .required("Descreva o comportamento do agente"),
  maxTokens: Yup.number()
    .min(10, "Mínimo 10 tokens")
    .max(32000, "Máximo 32000 tokens")
    .required("Obrigatório"),
  temperature: Yup.number()
    .min(0, "Mínimo 0")
    .max(2, "Máximo 2")
    .required("Obrigatório"),
  flowMode: Yup.string()
    .oneOf(["permanent", "temporary"], "Modo inválido")
    .required("Selecione o modo"),
});

const FlowBuilderAddIAAgentModal = ({ open, onSave, data, onUpdate, close }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMounted = useRef(true);

  const initialState = {
    provider: "openai",
    model: "gpt-4o",
    apiKey: "",
    baseUrl: "",
    prompt: "",
    maxTokens: 2000,
    temperature: 0.7,
    maxMessages: 20,
    flowMode: "permanent",
    // Modo temporário
    maxInteractions: 10,
    completionTimeout: 15,
    continueKeywords: ["continuar", "próximo", "prosseguir"],
    objective: "",
    autoCompleteOnObjective: false,
    queueId: 0,
    // Ferramentas/Functions
    enableTools: false,
    tools: [],
    // Memory/Context
    enableMemory: true,
    memoryType: "conversation",
    // Configurações avançadas
    topP: 1,
    frequencyPenalty: 0,
    presencePenalty: 0,
  };

  const [showApiKey, setShowApiKey] = useState(false);
  const [agentConfig, setAgentConfig] = useState(initialState);
  const [newKeyword, setNewKeyword] = useState("");
  const [labels, setLabels] = useState({
    title: "Adicionar Agente IA ao fluxo",
    btn: "Adicionar",
  });
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (open === "edit") {
      setLabels({
        title: "Editar Agente IA",
        btn: "Salvar",
      });
      const config = data?.data?.agentConfig || {};
      setAgentConfig({
        ...initialState,
        ...config,
      });
    } else if (open === "create") {
      setLabels({
        title: "Adicionar Agente IA ao fluxo",
        btn: "Adicionar",
      });
      setAgentConfig(initialState);
    }

    return () => {
      isMounted.current = false;
    };
  }, [open, data]);

  const handleClose = () => {
    setNewKeyword("");
    setActiveTab(0);
    close(null);
  };

  const handleSave = (values, { setSubmitting }) => {
    const configData = {
      ...values,
      maxInteractions:
        values.flowMode === "temporary" ? values.maxInteractions : null,
      completionTimeout:
        values.flowMode === "temporary" ? values.completionTimeout : null,
      continueKeywords:
        values.flowMode === "temporary" ? values.continueKeywords : [],
      objective: values.flowMode === "temporary" ? values.objective : "",
      autoCompleteOnObjective:
        values.flowMode === "temporary" ? values.autoCompleteOnObjective : false,
    };

    if (open === "edit") {
      onUpdate({
        ...data,
        data: { agentConfig: configData },
      });
    } else if (open === "create") {
      onSave({
        agentConfig: configData,
      });
    }
    handleClose();
    setSubmitting(false);
  };

  const addKeyword = (arrayHelpers, keyword) => {
    if (keyword.trim()) {
      arrayHelpers.push(keyword.trim());
      setNewKeyword("");
    }
  };

  const getProviderModels = (provider) => {
    return providers[provider]?.models || [];
  };

  const ProviderIcon = ({ provider, size = 24 }) => {
    const IconComponent = providers[provider]?.icon || FaRobot;
    return (
      <IconComponent
        style={{
          width: size,
          height: size,
          color: providers[provider]?.color || "#6b7280",
        }}
      />
    );
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open === "create" || open === "edit"}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        scroll="paper"
      >
        <DialogTitle>{labels.title}</DialogTitle>
        <Formik
          initialValues={agentConfig}
          enableReinitialize={true}
          validationSchema={IAAgentSchema}
          onSubmit={handleSave}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form style={{ width: "100%" }}>
              <DialogContent dividers>
                {/* TABS */}
                <Paper square style={{ marginBottom: 16 }}>
                  <Tabs
                    value={activeTab}
                    onChange={(e, v) => setActiveTab(v)}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth"
                  >
                    <Tab
                      label="Provedor & Modelo"
                      icon={<Settings />}
                      className={classes.providerTab}
                    />
                    <Tab
                      label="Comportamento"
                      icon={<Chat />}
                      className={classes.providerTab}
                    />
                    <Tab
                      label="Fluxo"
                      icon={<Timer />}
                      className={classes.providerTab}
                    />
                  </Tabs>
                </Paper>

                {/* TAB 0: Provedor & Modelo */}
                {activeTab === 0 && (
                  <div className={classes.providerTabPanel}>
                    <Typography
                      variant="subtitle1"
                      style={{ marginTop: 16, marginBottom: 8, fontWeight: "bold" }}
                    >
                      Selecione o Provedor de IA
                    </Typography>

                    <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
                      {Object.entries(providers).map(([key, config]) => (
                        <Box
                          key={key}
                          className={`${classes.providerCard} ${
                            values.provider === key
                              ? classes.providerCardSelected
                              : ""
                          }`}
                          onClick={() => {
                            setFieldValue("provider", key);
                            setFieldValue("model", config.models[0]?.id || "");
                          }}
                          style={{
                            borderColor:
                              values.provider === key ? config.color : undefined,
                            flex: "1 1 200px",
                            maxWidth: 250,
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <ProviderIcon provider={key} size={28} />
                            <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
                              {config.name}
                            </Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            style={{ display: "block", marginTop: 4 }}
                          >
                            {config.models.length} modelos disponíveis
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    <FormControl
                      fullWidth
                      margin="dense"
                      variant="outlined"
                      error={touched.model && Boolean(errors.model)}
                    >
                      <InputLabel>Modelo</InputLabel>
                      <Field as={Select} label="Modelo" name="model">
                        {getProviderModels(values.provider).map((model) => (
                          <MenuItem key={model.id} value={model.id}>
                            {model.name}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>

                    {values.provider !== "local" && (
                      <FormControl fullWidth margin="dense" variant="outlined">
                        <Field
                          as={TextField}
                          label={`API Key ${providers[values.provider]?.name}`}
                          name="apiKey"
                          type={showApiKey ? "text" : "password"}
                          error={touched.apiKey && Boolean(errors.apiKey)}
                          helperText={touched.apiKey && errors.apiKey}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          required
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowApiKey(!showApiKey)}
                                >
                                  {showApiKey ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </FormControl>
                    )}

                    {values.provider === "local" && (
                      <Field
                        as={TextField}
                        label="URL do Servidor (Ollama)"
                        name="baseUrl"
                        error={touched.baseUrl && Boolean(errors.baseUrl)}
                        helperText={
                          touched.baseUrl && errors.baseUrl ||
                          "Ex: http://localhost:11434"
                        }
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        placeholder="http://localhost:11434"
                      />
                    )}
                  </div>
                )}

                {/* TAB 1: Comportamento */}
                {activeTab === 1 && (
                  <div className={classes.providerTabPanel}>
                    <Field
                      as={TextField}
                      label="Prompt do Sistema"
                      name="prompt"
                      error={touched.prompt && Boolean(errors.prompt)}
                      helperText={touched.prompt && errors.prompt}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      required
                      rows={8}
                      multiline
                      placeholder="Descreva como o agente deve se comportar, que informações coletar, como responder, etc."
                    />

                    <div className={classes.multFieldLine}>
                      <Field
                        as={TextField}
                        label="Temperatura"
                        name="temperature"
                        error={touched.temperature && Boolean(errors.temperature)}
                        helperText={
                          (touched.temperature && errors.temperature) ||
                          "0 = conservador, 2 = criativo"
                        }
                        variant="outlined"
                        margin="dense"
                        type="number"
                        inputProps={{ step: "0.1", min: "0", max: "2" }}
                      />
                      <Field
                        as={TextField}
                        label="Max Tokens"
                        name="maxTokens"
                        error={touched.maxTokens && Boolean(errors.maxTokens)}
                        helperText={
                          (touched.maxTokens && errors.maxTokens) ||
                          "Tamanho máximo da resposta"
                        }
                        variant="outlined"
                        margin="dense"
                        type="number"
                      />
                      <Field
                        as={TextField}
                        label="Max Mensagens"
                        name="maxMessages"
                        error={touched.maxMessages && Boolean(errors.maxMessages)}
                        helperText={
                          (touched.maxMessages && errors.maxMessages) ||
                          "Histórico de contexto"
                        }
                        variant="outlined"
                        margin="dense"
                        type="number"
                      />
                    </div>

                    {/* Ferramentas (Function Calling) */}
                    <Accordion className={classes.accordion} style={{ marginTop: 16 }}>
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        className={classes.accordionSummary}
                      >
                        <Typography className={classes.sectionTitle}>
                          <Functions />
                          Ferramentas (Function Calling)
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails className={classes.accordionDetails}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.enableTools}
                              onChange={(e) =>
                                setFieldValue("enableTools", e.target.checked)
                              }
                              name="enableTools"
                            />
                          }
                          label="Habilitar chamada de funções"
                        />
                        <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 12 }}>
                          Permite que o agente execute ações no sistema (transferir fila, encerrar ticket, etc.) e funções customizadas via HTTP.
                        </Typography>

                        {values.enableTools && (
                          <FieldArray name="tools">
                            {(arrayHelpers) => {
                              const systemActions = [
                                { id: "transferir_fila", label: "Transferir Fila", desc: "Transfere o ticket para uma fila/setor", icon: SwapHoriz, color: "#2196f3", category: "action" },
                                { id: "transferir_atendente", label: "Transferir Atendente", desc: "Transfere para um atendente específico", icon: PersonOutline, color: "#1976d2", category: "action" },
                                { id: "atualizar_contato", label: "Atualizar Contato", desc: "Atualiza nome, email do contato", icon: PersonOutline, color: "#4caf50", category: "action" },
                                { id: "definir_variavel", label: "Definir Variável", desc: "Define uma variável do fluxo", icon: VpnKey, color: "#ff9800", category: "action" },
                                { id: "encerrar_ticket", label: "Encerrar Ticket", desc: "Encerra/fecha o ticket atual", icon: Close, color: "#f44336", category: "action" },
                                { id: "adicionar_tag", label: "Adicionar Tag", desc: "Adiciona uma tag ao ticket", icon: LocalOffer, color: "#9c27b0", category: "action" },
                                { id: "remover_tag", label: "Remover Tag", desc: "Remove uma tag do ticket", icon: LocalOffer, color: "#7b1fa2", category: "action" },
                                { id: "adicionar_nota_interna", label: "Nota Interna", desc: "Adiciona nota visível só para equipe", icon: Info, color: "#e91e63", category: "action" },
                                { id: "alterar_status_ticket", label: "Alterar Status", desc: "Altera status do ticket (aberto, pendente, fechado)", icon: ConfirmationNumber, color: "#ff5722", category: "action" },
                                { id: "listar_filas", label: "Listar Filas", desc: "Consulta setores disponíveis", icon: ListAlt, color: "#00bcd4", category: "query" },
                                { id: "listar_tags", label: "Listar Tags", desc: "Consulta tags cadastradas", icon: LocalOffer, color: "#607d8b", category: "query" },
                                { id: "listar_tickets", label: "Listar Tickets", desc: "Histórico de tickets do contato", icon: ConfirmationNumber, color: "#795548", category: "query" },
                                { id: "dados_ticket_atual", label: "Dados do Ticket", desc: "Info do ticket/conversa atual", icon: Info, color: "#3f51b5", category: "query" },
                                { id: "historico_mensagens", label: "Histórico Mensagens", desc: "Últimas mensagens da conversa", icon: Chat, color: "#009688", category: "query" },
                              ];

                              const actionTools = systemActions.filter(a => a.category === "action");
                              const queryTools = systemActions.filter(a => a.category === "query");

                              const renderToolCard = (action) => {
                                const Icon = action.icon;
                                const isEnabled = (values.tools || []).some(t => t.type === "system" && t.id === action.id);
                                return (
                                  <Box
                                    key={action.id}
                                    onClick={() => {
                                      if (isEnabled) {
                                        const idx = (values.tools || []).findIndex(t => t.type === "system" && t.id === action.id);
                                        if (idx >= 0) arrayHelpers.remove(idx);
                                      } else {
                                        arrayHelpers.push({
                                          type: "system",
                                          id: action.id,
                                          name: action.label,
                                          description: action.desc,
                                        });
                                      }
                                    }}
                                    style={{
                                      border: isEnabled ? `2px solid ${action.color}` : "2px solid transparent",
                                      backgroundColor: isEnabled
                                        ? `${action.color}12`
                                        : theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
                                      borderRadius: 10,
                                      padding: "12px 10px",
                                      cursor: "pointer",
                                      transition: "all 0.15s ease",
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      textAlign: "center",
                                      minHeight: 100,
                                      justifyContent: "center",
                                      position: "relative",
                                      flex: "1 1 calc(25% - 8px)",
                                      minWidth: 0,
                                      maxWidth: "calc(25% - 8px)",
                                    }}
                                  >
                                    {isEnabled && (
                                      <Box
                                        style={{
                                          position: "absolute",
                                          top: 6,
                                          right: 6,
                                          width: 16,
                                          height: 16,
                                          borderRadius: "50%",
                                          backgroundColor: action.color,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <Typography style={{ color: "#fff", fontSize: 10, fontWeight: "bold", lineHeight: 1 }}>
                                          &#10003;
                                        </Typography>
                                      </Box>
                                    )}
                                    <Box
                                      style={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: 8,
                                        backgroundColor: isEnabled ? action.color : `${action.color}30`,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        marginBottom: 6,
                                        transition: "all 0.15s ease",
                                      }}
                                    >
                                      <Icon style={{ color: isEnabled ? "#fff" : action.color, fontSize: 20 }} />
                                    </Box>
                                    <Typography variant="caption" style={{
                                      fontWeight: 600,
                                      fontSize: 11,
                                      lineHeight: 1.2,
                                      color: isEnabled ? theme.palette.text.primary : theme.palette.text.secondary,
                                    }}>
                                      {action.label}
                                    </Typography>
                                    <Typography variant="caption" style={{
                                      fontSize: 9,
                                      color: theme.palette.text.disabled,
                                      lineHeight: 1.2,
                                      marginTop: 2,
                                    }}>
                                      {action.desc}
                                    </Typography>
                                  </Box>
                                );
                              };

                              return (
                              <div>
                                {/* Ações do sistema */}
                                <Typography variant="subtitle2" style={{ fontWeight: "bold", marginBottom: 6, marginTop: 8, display: "flex", alignItems: "center", gap: 6 }}>
                                  <Functions style={{ fontSize: 18 }} />
                                  Ações
                                </Typography>
                                <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 10 }}>
                                  A IA executa ações que alteram dados no sistema.
                                </Typography>
                                <Box style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
                                  {actionTools.map(renderToolCard)}
                                </Box>

                                {/* Consultas */}
                                <Typography variant="subtitle2" style={{ fontWeight: "bold", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                                  <Search style={{ fontSize: 18 }} />
                                  Consultas
                                </Typography>
                                <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 10 }}>
                                  A IA consulta dados para tomar decisões ou informar o cliente.
                                </Typography>
                                <Box style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
                                  {queryTools.map(renderToolCard)}
                                </Box>

                                {/* Funções customizadas (HTTP) */}
                                <Typography variant="subtitle2" style={{ fontWeight: "bold", marginBottom: 8, marginTop: 16 }}>
                                  Funções Customizadas (HTTP/Webhook)
                                </Typography>
                                <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 8 }}>
                                  A IA chamará estas URLs quando precisar de dados ou executar ações externas.
                                </Typography>

                                {(values.tools || [])
                                  .filter(t => t.type === "custom")
                                  .map((tool, idx) => {
                                    const realIdx = (values.tools || []).findIndex(t => t === tool);
                                    return (
                                      <Paper key={idx} variant="outlined" style={{ padding: 12, marginBottom: 8 }}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                          <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
                                            {tool.name || "Nova Função"}
                                          </Typography>
                                          <IconButton size="small" onClick={() => arrayHelpers.remove(realIdx)}>
                                            <Delete fontSize="small" color="error" />
                                          </IconButton>
                                        </Box>
                                        <TextField
                                          label="Nome da Função"
                                          value={tool.name || ""}
                                          onChange={(e) => setFieldValue(`tools[${realIdx}].name`, e.target.value)}
                                          variant="outlined"
                                          size="small"
                                          fullWidth
                                          margin="dense"
                                          placeholder="Ex: consultar_estoque"
                                          helperText="Sem espaços, use underscores"
                                        />
                                        <TextField
                                          label="Descrição"
                                          value={tool.description || ""}
                                          onChange={(e) => setFieldValue(`tools[${realIdx}].description`, e.target.value)}
                                          variant="outlined"
                                          size="small"
                                          fullWidth
                                          margin="dense"
                                          placeholder="Ex: Consulta o estoque de um produto pelo nome"
                                          helperText="Descreva quando a IA deve usar esta função"
                                        />
                                        <TextField
                                          label="URL do Webhook"
                                          value={tool.url || ""}
                                          onChange={(e) => setFieldValue(`tools[${realIdx}].url`, e.target.value)}
                                          variant="outlined"
                                          size="small"
                                          fullWidth
                                          margin="dense"
                                          placeholder="https://seu-webhook.com/api/estoque"
                                        />
                                        <FormControl fullWidth margin="dense" variant="outlined" size="small">
                                          <InputLabel>Método HTTP</InputLabel>
                                          <Select
                                            label="Método HTTP"
                                            value={tool.method || "POST"}
                                            onChange={(e) => setFieldValue(`tools[${realIdx}].method`, e.target.value)}
                                          >
                                            <MenuItem value="GET">GET</MenuItem>
                                            <MenuItem value="POST">POST</MenuItem>
                                          </Select>
                                        </FormControl>
                                        <TextField
                                          label="Parâmetros (JSON Schema)"
                                          value={tool.parameters || ""}
                                          onChange={(e) => setFieldValue(`tools[${realIdx}].parameters`, e.target.value)}
                                          variant="outlined"
                                          size="small"
                                          fullWidth
                                          margin="dense"
                                          multiline
                                          rows={3}
                                          placeholder='{"produto": {"type": "string", "description": "Nome do produto"}}'
                                          helperText="Defina os parâmetros que a IA deve enviar em formato JSON Schema"
                                        />
                                      </Paper>
                                    );
                                  })}

                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<Add />}
                                  onClick={() =>
                                    arrayHelpers.push({
                                      type: "custom",
                                      name: "",
                                      description: "",
                                      url: "",
                                      method: "POST",
                                      parameters: "",
                                    })
                                  }
                                  style={{ marginTop: 8 }}
                                >
                                  Adicionar Função Customizada
                                </Button>
                              </div>
                              );
                            }}
                          </FieldArray>
                        )}
                      </AccordionDetails>
                    </Accordion>

                    {/* Parâmetros Avançados */}
                    <Accordion className={classes.accordion}>
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        className={classes.accordionSummary}
                      >
                        <Typography className={classes.sectionTitle}>
                          <Settings />
                          Parâmetros Avançados
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails className={classes.accordionDetails}>
                        <div className={classes.multFieldLine}>
                          <Field
                            as={TextField}
                            label="Top P"
                            name="topP"
                            helperText="Nucleus sampling (0-1)"
                            variant="outlined"
                            margin="dense"
                            type="number"
                            inputProps={{ step: "0.1", min: "0", max: "1" }}
                          />
                          <Field
                            as={TextField}
                            label="Frequency Penalty"
                            name="frequencyPenalty"
                            helperText="Penaliza repetição (-2 a 2)"
                            variant="outlined"
                            margin="dense"
                            type="number"
                            inputProps={{ step: "0.1", min: "-2", max: "2" }}
                          />
                          <Field
                            as={TextField}
                            label="Presence Penalty"
                            name="presencePenalty"
                            helperText="Incentiva novos tópicos (-2 a 2)"
                            variant="outlined"
                            margin="dense"
                            type="number"
                            inputProps={{ step: "0.1", min: "-2", max: "2" }}
                          />
                        </div>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.enableMemory}
                              onChange={(e) =>
                                setFieldValue("enableMemory", e.target.checked)
                              }
                              name="enableMemory"
                            />
                          }
                          label="Habilitar memória de conversa"
                          style={{ marginTop: 8 }}
                        />
                      </AccordionDetails>
                    </Accordion>
                  </div>
                )}

                {/* TAB 2: Fluxo */}
                {activeTab === 2 && (
                  <div className={classes.providerTabPanel}>
                    <FormControl component="fieldset" margin="normal">
                      <FormLabel component="legend">Modo de Funcionamento</FormLabel>
                      <RadioGroup
                        name="flowMode"
                        value={values.flowMode}
                        onChange={(e) => setFieldValue("flowMode", e.target.value)}
                      >
                        <FormControlLabel
                          value="permanent"
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="body1">
                                <strong>Permanente</strong> - Encerrar fluxo aqui
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                O usuário fica conversando com a IA até pedir
                                transferência ou encerrar
                              </Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          value="temporary"
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="body1">
                                <strong>Temporário</strong> - Volta ao fluxo depois
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                A IA executa uma tarefa específica e depois retorna
                                ao fluxo normal
                              </Typography>
                            </Box>
                          }
                        />
                      </RadioGroup>
                    </FormControl>

                    {values.flowMode === "temporary" && (
                      <div className={classes.temporarySettings}>
                        <Typography variant="h6" gutterBottom>
                          Configurações do Modo Temporário
                        </Typography>

                        <Field
                          as={TextField}
                          label="Máximo de Interações"
                          name="maxInteractions"
                          helperText="Número máximo de mensagens antes de voltar ao fluxo (0 = ilimitado)"
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          type="number"
                          inputProps={{ min: 0, max: 50 }}
                        />

                        <Field
                          as={TextField}
                          label="Timeout (minutos)"
                          name="completionTimeout"
                          helperText="Tempo limite antes de voltar ao fluxo (0 = sem limite)"
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          type="number"
                          inputProps={{ min: 0, max: 60 }}
                        />

                        <FormControl fullWidth margin="dense">
                          <Typography variant="subtitle2" gutterBottom>
                            Palavras-chave para Continuar Fluxo
                          </Typography>
                          <FieldArray name="continueKeywords">
                            {(arrayHelpers) => (
                              <div>
                                <Box display="flex" gap={1} alignItems="center" mb={1}>
                                  <TextField
                                    variant="outlined"
                                    size="small"
                                    placeholder="Digite uma palavra-chave"
                                    value={newKeyword}
                                    onChange={(e) => setNewKeyword(e.target.value)}
                                    onKeyPress={(e) => {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        addKeyword(arrayHelpers, newKeyword);
                                      }
                                    }}
                                  />
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<Add />}
                                    onClick={() => addKeyword(arrayHelpers, newKeyword)}
                                    disabled={!newKeyword.trim()}
                                  >
                                    Adicionar
                                  </Button>
                                </Box>
                                <Box display="flex" flexWrap="wrap">
                                  {values.continueKeywords.map((keyword, index) => (
                                    <Chip
                                      key={index}
                                      label={keyword}
                                      className={classes.keywordChip}
                                      onDelete={() => arrayHelpers.remove(index)}
                                      deleteIcon={<Delete />}
                                      variant="outlined"
                                      size="small"
                                    />
                                  ))}
                                </Box>
                              </div>
                            )}
                          </FieldArray>
                        </FormControl>

                        <Field
                          as={TextField}
                          label="Objetivo da IA"
                          name="objective"
                          helperText="Descreva o que a IA deve completar"
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          multiline
                          rows={2}
                          placeholder="Ex: Coletar nome, email e telefone do cliente"
                        />

                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.autoCompleteOnObjective}
                              onChange={(e) =>
                                setFieldValue(
                                  "autoCompleteOnObjective",
                                  e.target.checked
                                )
                              }
                              name="autoCompleteOnObjective"
                            />
                          }
                          label="Auto completar quando atingir objetivo"
                        />

                        <Field
                          as={TextField}
                          label="ID da Fila (para transferência)"
                          name="queueId"
                          helperText="ID da fila para onde transferir se usuário pedir atendente (0 = não transferir)"
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          type="number"
                          inputProps={{ min: 0 }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  variant="outlined"
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  className={classes.btnWrapper}
                  disabled={isSubmitting}
                >
                  {labels.btn}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default FlowBuilderAddIAAgentModal;
